import { useEffect, useState, useCallback } from 'react'
import { useWeb3React } from '@web3-react/core'
import { useDispatch } from 'react-redux'
import { fetchPoolsUserDataAsync } from 'state/pools'
import BigNumber from 'bignumber.js'
import { getBowAddress, getXbowAddress } from 'utils/addressHelpers'
import { xbowStake, xbowUnstake } from 'utils/callHelpers'
import useRefresh from './useRefresh'
import { useERC20, useXbow } from './useContract'
import useGasBoost from './useGasBoost'

/** @param refresh change it to re-read right away (e.g. after a stake or unstake) */
const useBowInfo = (refresh?: boolean) => {
  const [totalLockedBow, setTotalLockedBow] = useState(new BigNumber(0))
  const [xbowRatio, setXbowRatio] = useState(new BigNumber(0))
  const [delayToWithdraw, setDelayToWithdraw] = useState(0)
  const { account } = useWeb3React()
  const { fastRefresh } = useRefresh()
  const xbowContract = useXbow()
  const bowContract = useERC20(getBowAddress())

  useEffect(() => {
    const fetchInfo = async () => {
      const [res0, res1, res2] = await Promise.allSettled([
        bowContract.methods.balanceOf(getXbowAddress()).call(),
        xbowContract.methods.xTOONForTOON(10000).call(),
        xbowContract.methods.delayToWithdraw().call(),
      ])
      setTotalLockedBow(new BigNumber(res0.status === 'fulfilled' ? res0.value : '0').div(1e18))
      setXbowRatio(new BigNumber(res1.status === 'fulfilled' ? res1.value : '0').div(1e4))
      setDelayToWithdraw(Number(res2.status === 'fulfilled' ? res2.value : 0))
    }
    fetchInfo()
  }, [account, xbowContract, bowContract, fastRefresh, refresh])

  return { totalLockedBow, xbowRatio, delayToWithdraw }
}

/** @param refresh change it to re-read right away (e.g. after a stake or unstake) */
export const useUserInfo = (refresh?: boolean) => {
  const [bowBalance, setBowBalance] = useState(new BigNumber(0))
  const [xbowBalance, setXbowBalance] = useState(new BigNumber(0))
  const [claimableAmount, setClaimableAmount] = useState(new BigNumber(0))
  const [stakedTime, setStakedTime] = useState(0)
  const { account } = useWeb3React()
  const { fastRefresh } = useRefresh()
  const xbowContract = useXbow()
  const bowContract = useERC20(getBowAddress())

  useEffect(() => {
    const fetchInfo = async () => {
      const [res0, res1, res2, res3] = await Promise.allSettled([
        bowContract.methods.balanceOf(account).call(),
        xbowContract.methods.balanceOf(account).call(),
        xbowContract.methods.TOONBalance(account).call(),
        xbowContract.methods.stakedTime(account).call(),
      ])
      setBowBalance(new BigNumber(res0.status === 'fulfilled' ? res0.value : '0'))
      setXbowBalance(new BigNumber(res1.status === 'fulfilled' ? res1.value : '0'))
      setClaimableAmount(new BigNumber(res2.status === 'fulfilled' ? res2.value : '0').div(1e18))
      setStakedTime(Number(res3.status === 'fulfilled' ? res3.value : 0))
    }
    if (account) {
      fetchInfo()
    }
  }, [account, xbowContract, bowContract, fastRefresh, refresh])

  return { bowBalance, xbowBalance, claimableAmount, stakedTime }
}

export const useXbowStake = () => {
  const { account } = useWeb3React()
  const dispatch = useDispatch()
  const xbowContract = useXbow()

  const { gasBoostedPrice } = useGasBoost()
  const handleStake = useCallback(
    async (amount: string) => {
      const txHash = await xbowStake(xbowContract, amount, account, gasBoostedPrice)
      console.info(txHash)
      // The xTOON pool's wallet balance changed
      dispatch(fetchPoolsUserDataAsync(account))
    },
    [account, dispatch, xbowContract, gasBoostedPrice],
  )

  return { onStake: handleStake }
}

export const useXbowUnstake = () => {
  const { account } = useWeb3React()
  const dispatch = useDispatch()
  const xbowContract = useXbow()
  const { gasBoostedPrice } = useGasBoost()

  const handleUnstake = useCallback(
    async (amount: string) => {
      const txHash = await xbowUnstake(xbowContract, amount, account, gasBoostedPrice)
      console.info(txHash)
      // The xTOON pool's wallet balance changed
      dispatch(fetchPoolsUserDataAsync(account))
    },
    [account, dispatch, xbowContract, gasBoostedPrice],
  )

  return { onUnstake: handleUnstake }
}

export default useBowInfo
